<template>
  <modal
    :class="$style.addMeetings"
    :header-text="headerText"
    hide-footer="true"
    @close="close()"
  >
    <Loader
      v-if="isPending"
      :class="$style.meetingsConfig"
    />
    <div
      v-else
      :class="$style.meetingsConfig"
    >
      <button
        v-if="meeting"
        :class="[$style.button, $style.buttonDelete]"
        @click="deleteMeeting"
      >
        {{ $t('global.delete') }}
      </button>
      <div :class="$style.container">
        <div>{{ $t('settings.typeOfMeeting') }}:</div>
        <div :class="$style.typeMeeting">
          <img
            :title="$t('settings.changeType')"
            src="@/assets/images/icons/computer/wrench.svg"
            :class="$style.changeType"
            @click="toggleTypesMeetings"
          >
          <vue-select
            v-model="formField.type"
            :options="types"
            :reduce="({ id }) => id"
            label="type"
          />
        </div>

        <div>{{ $t('settings.meetingName') }}:</div>
        <input
          id="caption"
          v-model="formField.caption"
          name="caption"
          :class="$style.input"
        >
        <div>{{ $t('settings.regularity') }}:</div>
        <vue-select
          v-model="formField.regularity"
          :options="regFrequency"
          :reduce="({ id }) => id"
          label="name"
        />
      </div>
      <div>
        <div :class="$style.wrapper">
          {{ $t('global.time') }}:
        </div>
        <div :class="$style.wrapper">
          <div>{{ $t('global.date') }}:</div>
          <date-picker
            v-model="formField.date"
            format="DD.MM.YYYY"
          />
          <div>{{ $t('global.from') }}</div>
          <date-picker
            v-model="formField.timeBeg"
            type="time"
            format="HH:mm:ss"
          />
          <div>{{ $t('global.to') }}</div>
          <date-picker
            v-model="formField.timeEnd"
            type="time"
            format="HH:mm:ss"
          />
        </div>
      </div>
      <div :class="$style.container">
        <button
          :class="$style.button"
          :disabled="!!!differentKeys.length"
          @click="save()"
        >
          {{ $t('global.save') }}
        </button>
        <button
          :class="$style.button"
          @click="close()"
        >
          {{ $t('global.cancel') }}
        </button>
      </div>
    </div>
    <add-types-meetings-modal
      v-if="isOpenTypesMeetings"
      :types="types"
      @close="toggleTypesMeetings"
      @update="$emit('updateType')"
    />
  </modal>
</template>

<script>
  import Modal from '@/components/common/modal/settings-modal.vue';
  import Loader from '@/components/common/blocks/loader.vue';
  import VueSelect from 'vue-select';
  import DatePicker from 'vue2-datepicker';
  import { openToast, errorToast, TOAST_TYPE } from '@/utils/open-toast';
  import { deleteMeeting, saveNewMeeting, changeMeeting } from '@/api/methods/settings';
  import { createDateAtString } from '@/helpers/format-date';
  import { isEqual, cloneDeep, keys } from 'lodash';

  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import addTypesMeetingsModal from './add-types-meetings-modal.vue';

  dayjs.extend(utc);
  export default {
    components: { Modal, Loader, VueSelect, DatePicker, addTypesMeetingsModal },
    props: ['meeting', 'types'],
    data() {
      return {
        isOpenTypesMeetings: false,
        initData: {},
        formField: {
          type: null,
          regularity: null,
          caption: null,
          date: dayjs().toDate(),
          timeBeg: dayjs().add(1, 'hour').toDate(),
          timeEnd: dayjs().add(2, 'hour').toDate(),
        },
        isPending: false,
        regFrequency: [
          {
            id: 0,
            name: this.$t('settings.regFrequency.once'),
          },
          {
            id: 1,
            name: this.$t('settings.regFrequency.daily'),
          },
          {
            id: 2,
            name: this.$t('settings.regFrequency.weekly'),
          },
        ],
      };
    },
    computed: {
      differentKeys() {
        return keys(this.formField).filter((key) => !isEqual(this.formField[key], this.initData[key]));
      },
      headerText() {
        if (this.meeting) {
          const currentKey = `settings.meetings.${this.meeting.type}`;
          return this.$te(currentKey) ? this.$t(currentKey) : this.meeting.name || '';
        }
        if (this.types[0]?.id) return this.$t(`settings.meetings.${this.types[0]?.id}`);
        return '';
      },
    },
    async created() {
      if (this.meeting) {
        const { caption, date, timeBeg, timeEnd, regularity, type } = this.meeting;

        this.$set(this.formField, 'type', type);
        this.$set(this.formField, 'caption', caption);
        this.$set(this.formField, 'regularity', regularity);
        this.$set(this.formField, 'date', dayjs(date).toDate());
        this.$set(this.formField, 'timeBeg', createDateAtString(timeBeg));
        this.$set(this.formField, 'timeEnd', createDateAtString(timeEnd));
      }
      this.initData = cloneDeep(this.formField);
    },
    methods: {
      toggleTypesMeetings() {
        this.isOpenTypesMeetings = !this.isOpenTypesMeetings;
      },
      async deleteMeeting() {
        this.isPending = true;
        const { data } = await deleteMeeting(this.meeting.id);
        this.isPending = false;

        if (data?.success) {
          openToast(this.$t('toast.meetingDeletedSuccessfully'), TOAST_TYPE.SUCCESS);
          this.$emit('update');
          this.close();
        } else {
          errorToast();
        }
      },
      async save() {
        let message = null;

        if (!this.formField.type) message = this.$t('toast.selectTypeOfMeeting');
        else if (!this.formField.caption) message = this.$t('toast.сhooseMeetingName');
        else if (this.formField.regularity === null) message = this.$t('toast.chooseRegularity');

        if (message) {
          openToast(message, TOAST_TYPE.INFO);
          message = null;
          return;
        }

        this.isPending = true;
        let result;
        if (this.meeting) {
          const params = {
            id: this.meeting.id,
          };
          this.differentKeys.forEach((key) => {
            const value = this.formField[key];
            switch (key) {
              case 'date':
                params[key] = dayjs(value).utc(true);
                break;
              case 'timeBeg':
              case 'timeEnd':
                params.timeBeg = dayjs(this.formField.timeBeg).utc(true).format('HH:mm:ss');
                params.timeEnd = dayjs(this.formField.timeEnd).utc(true).format('HH:mm:ss');
                break;
              default:
                params[key] = value;
                break;
            }
          });
          const { data } = await changeMeeting(params);
          result = data;
        } else {
          const { caption, date, timeBeg, timeEnd, regularity, type } = this.formField;
          const params = {
            id: this.$route.params.computer,
            caption,
            date: dayjs(date).utc(true),
            timeBeg: dayjs(timeBeg).utc(true).format('HH:mm:ss'),
            timeEnd: dayjs(timeEnd).utc(true).format('HH:mm:ss'),
            regularity,
            type,
          };
          const { data } = await saveNewMeeting(params);
          result = data;
        }
        this.isPending = false;

        if (result?.success) {
          openToast(this.$t('toast.meetingAddedSuccessfully'), TOAST_TYPE.SUCCESS);
          this.close();
          this.$emit('update');
        } else {
          errorToast();
        }
      },
      close() {
        this.$emit('close');
      },
    },
  };
</script>

<style lang="scss" module>
  .addMeetings {
    display: flex;
    z-index: 2;
  }
  .meetingsConfig {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 250px;
  }
  .container {
    display: grid;
    grid-template-columns: auto auto;
    gap: 10px;
    align-items: center;
    justify-content: space-around;
  }

  .wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 10px;
  }
  .input {
    height: 30px;
    padding: 0 7px;
    background: none;
    border: 1px solid rgba(60, 60, 60, 0.26);
    border-radius: 4px;
    color: #333;
    line-height: 1.4;
  }
  .button {
    cursor: pointer;
    background: #2985bf;
    border: none;
    border-radius: 2px;
    font: inherit;
    font-size: 14px;
    text-align: center;
    color: #ffffff;
    opacity: 1;
    height: 100%;
    transition: all 0.3 ease-in-out;
    padding: 10px;
    &:disabled {
      background: $branchColor;
      border: $branchColor;
    }
  }
  .button:hover {
    opacity: 0.8;
    &:disabled {
      opacity: 1;
      background: $branchColor;
      border: $branchColor;
    }
  }
  .button:active {
    background: rgb(73, 173, 106);
    &:disabled {
      background: $branchColor;
      border: $branchColor;
    }
  }
  .buttonDelete {
    background: $red;
    border: $red;
    width: 100px;
    margin: 0 50px 0 auto;
  }
  .typeMeeting {
    position: relative;
  }
  .changeType {
    position: absolute;
    width: 20px;
    height: 20px;
    cursor: pointer;
    left: -20px;
    top: 50%;
    transform: translate(-50%, -50%);
  }
</style>
